// pages/AllProductsPage.js - Fixed Product Display
import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useProducts } from '../hooks/useProducts';
import { 
  ShoppingCart, 
  Crown, 
  Search, 
  X, 
  Filter, 
  ChevronDown,
  ArrowLeft,
  Loader,
  Tag,
  DollarSign,
  Star,
  Heart,
  Truck,
  Shield,
  Eye,
  Clock,
  TrendingUp
} from 'lucide-react';
import { toast } from 'react-toastify';

const AllProductsPage = () => {
  const { sectionId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { products: allProducts, loading: productsLoading } = useProducts();
  
  // State for filters
  const [searchInput, setSearchInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sortBy, setSortBy] = useState('default');
  const [selectedTags, setSelectedTags] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: 0, max: 50000 });
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const searchRef = React.useRef(null);
  
  // Recent searches state
  const [recentSearches, setRecentSearches] = useState(() => {
    try { 
      const saved = localStorage.getItem('recent_searches');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });

  // Check mobile on resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get all products - FIXED: Properly extract products from nested structure
  const allProductsList = useMemo(() => {
    if (!allProducts) return [];
    
    // Log to debug
    console.log('All products from hook:', allProducts);
    
    const productsList = [];
    
    // Check if products are in the expected structure
    if (allProducts.occasion && Array.isArray(allProducts.occasion)) {
      productsList.push(...allProducts.occasion);
    }
    if (allProducts.luxury && Array.isArray(allProducts.luxury)) {
      productsList.push(...allProducts.luxury);
    }
    if (allProducts.exotic && Array.isArray(allProducts.exotic)) {
      productsList.push(...allProducts.exotic);
    }
    if (allProducts.decor && Array.isArray(allProducts.decor)) {
      productsList.push(...allProducts.decor);
    }
    
    // If no products found in nested structure, try direct array
    if (productsList.length === 0 && Array.isArray(allProducts)) {
      productsList.push(...allProducts);
    }
    
    // If still no products, try object values
    if (productsList.length === 0 && typeof allProducts === 'object') {
      Object.values(allProducts).forEach(category => {
        if (Array.isArray(category)) {
          productsList.push(...category);
        }
      });
    }
    
    console.log('Products list:', productsList.length);
    return productsList;
  }, [allProducts]);

  // Section titles
  const getSectionTitle = () => {
    const titles = {
      'occasions': 'Occasion Flowers',
      'luxury': 'Luxury Collection',
      'exotic': 'Exotic Blooms',
      'decor': 'Decoration Themes',
      'bestsellers': 'Best Sellers',
      'recommended': 'Recommended for You',
      'all': 'All Products'
    };
    return titles[sectionId] || 'Products';
  };

  const getSectionDescription = () => {
    const descriptions = {
      'occasions': 'Perfect flowers for every special moment',
      'luxury': 'Premium arrangements for extraordinary occasions',
      'exotic': 'Rare and unique flowers from around the world',
      'decor': 'Transform any space with our decoration themes',
      'bestsellers': 'Most loved by our customers',
      'recommended': 'Handpicked just for you',
      'all': 'Explore our complete collection'
    };
    return descriptions[sectionId] || 'Beautiful flowers for every occasion';
  };

  // Filter products based on section - FIXED: Show all products by default
  const sectionFilteredProducts = useMemo(() => {
    if (!allProductsList.length) return [];
    
    let filtered = [...allProductsList];
    
    // Apply section-specific filters
    switch(sectionId) {
      case 'bestsellers':
        filtered = filtered.filter(p => 
          p.tag === 'Best Seller' || 
          p.tag === 'Trending' || 
          p.tag === 'Popular' ||
          (p.rating && p.rating > 4)
        );
        break;
      case 'recommended':
        filtered = filtered.filter(p => 
          p.tag === 'Recommended' || 
          p.tag === 'Featured' ||
          p.tag === 'New Arrival'
        );
        break;
      case 'occasions':
        // Show all occasion flowers
        filtered = filtered.filter(p => p.category === 'occasion' || p.occasion);
        break;
      case 'luxury':
        filtered = filtered.filter(p => p.tag === 'Luxury' || p.price > 2000);
        break;
      case 'exotic':
        filtered = filtered.filter(p => p.tag === 'Exotic' || p.origin === 'Imported');
        break;
      case 'decor':
        filtered = filtered.filter(p => p.category === 'decor' || p.tag === 'Decoration');
        break;
      default:
        // Show all products
        break;
    }
    
    // If filtered is empty for bestsellers/recommended, show some products
    if (filtered.length === 0 && (sectionId === 'bestsellers' || sectionId === 'recommended')) {
      filtered = [...allProductsList].slice(0, 12);
    }
    
    return filtered;
  }, [allProductsList, sectionId]);

  // Get all unique tags from filtered products
  const allTags = useMemo(() => {
    const tags = new Set();
    sectionFilteredProducts.forEach(product => {
      if (product.tag) tags.add(product.tag);
    });
    return Array.from(tags);
  }, [sectionFilteredProducts]);

  // Search suggestions
  const suggestions = useMemo(() => {
    if (!searchInput.trim() || !sectionFilteredProducts.length) return [];
    const query = searchInput.toLowerCase();
    return sectionFilteredProducts
      .filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.tag?.toLowerCase().includes(query)
      )
      .slice(0, 5);
  }, [searchInput, sectionFilteredProducts]);

  // Popular search terms
  const popularSearches = [
    { icon: TrendingUp, label: 'Birthday Flowers', query: 'birthday' },
    { icon: Heart, label: 'Red Roses', query: 'roses' },
    { icon: Crown, label: 'Luxury Bouquets', query: 'luxury' },
    { icon: Tag, label: 'Anniversary', query: 'anniversary' }
  ];

  const addToRecentSearches = (query) => {
    const updated = [query, ...recentSearches.filter(s => s !== query)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recent_searches', JSON.stringify(updated));
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recent_searches');
  };

  const handleSearchSubmit = (query = searchInput) => {
    if (query.trim()) {
      addToRecentSearches(query.trim());
      setSearchInput(query);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (product) => {
    addToRecentSearches(product.name);
    navigate(`/product/${product.id}`);
    setShowSuggestions(false);
    setSearchInput('');
  };

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = [...sectionFilteredProducts];
    
    if (searchInput.trim()) {
      const query = searchInput.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.tag?.toLowerCase().includes(query)
      );
    }
    
    if (selectedTags.length > 0) {
      filtered = filtered.filter(p => selectedTags.includes(p.tag));
    }
    
    filtered = filtered.filter(p => p.price >= priceRange.min && p.price <= priceRange.max);
    
    switch(sortBy) {
      case 'price-low': filtered.sort((a, b) => a.price - b.price); break;
      case 'price-high': filtered.sort((a, b) => b.price - a.price); break;
      case 'name-asc': filtered.sort((a, b) => a.name.localeCompare(b.name)); break;
      case 'discount': filtered.sort((a, b) => {
        const da = a.oldPrice ? ((a.oldPrice - a.price) / a.oldPrice) : 0;
        const db = b.oldPrice ? ((b.oldPrice - b.price) / b.oldPrice) : 0;
        return db - da;
      }); break;
      default: filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return filtered;
  }, [sectionFilteredProducts, searchInput, selectedTags, priceRange, sortBy]);

  const handleFilterChange = (callback) => {
    setIsFiltering(true);
    setTimeout(() => { callback(); setIsFiltering(false); }, 300);
  };

  const handleTagToggle = (tag) => {
    handleFilterChange(() => {
      setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);
    });
  };

  const handleSortChange = (value) => {
    handleFilterChange(() => setSortBy(value));
  };

  const clearFilters = () => {
    handleFilterChange(() => {
      setSearchInput('');
      setSelectedTags([]);
      setSortBy('default');
      setPriceRange({ min: 0, max: 50000 });
      setShowSuggestions(false);
    });
  };

  const clearSearch = () => {
    setSearchInput('');
    setShowSuggestions(false);
  };

  const hasActiveFilters = searchInput || selectedTags.length > 0 || sortBy !== 'default';

  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    if (!user) { toast.info('Please login'); navigate('/login'); return; }
    
    const cartItem = {
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      quantity: 1,
      variantId: 'standard',
      variantName: 'Standard'
    };
    addToCart(cartItem);
    toast.success(`${product.name} added to cart!`);
  };

  const handleBuyNow = (product, e) => {
    e.stopPropagation();
    if (!user) { toast.info('Please login'); navigate('/login'); return; }
    
    const orderData = {
      items: [{
        id: product.id,
        name: product.name,
        price: product.price,
        quantity: 1,
        image: product.image,
        variantId: 'standard',
        variantName: 'Standard'
      }],
      totalAmount: product.price,
      paymentMethod: 'Online Payment'
    };
    sessionStorage.setItem('buyNowProduct', JSON.stringify(orderData));
    navigate('/checkout');
  };

  // Generate rating for product
  const getProductRating = (product) => {
    if (product.rating) return product.rating;
    return (3.5 + Math.random() * 1.5).toFixed(1);
  };

  const getReviewCount = () => {
    return Math.floor(Math.random() * 5000) + 100;
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  // Desktop Product Card
  const DesktopProductCard = ({ product }) => {
    const discountPercent = product.oldPrice && product.oldPrice > product.price
      ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) : 0;
    const rating = getProductRating(product);
    const reviewCount = getReviewCount();
    const [isWishlisted, setIsWishlisted] = useState(false);

    return (
      <div
        onClick={() => handleProductClick(product.id)}
        style={{
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(220,38,38,0.1)',
          transition: 'box-shadow 0.2s, transform 0.2s',
          cursor: 'pointer',
          position: 'relative',
          height: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 8px 20px rgba(220,38,38,0.15)';
          e.currentTarget.style.transform = 'translateY(-4px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = 'none';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <button
          onClick={(e) => { e.stopPropagation(); setIsWishlisted(!isWishlisted); }}
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Heart size={16} fill={isWishlisted ? '#dc2626' : 'none'} color={isWishlisted ? '#dc2626' : '#999'} />
        </button>

        <div style={{ position: 'relative', padding: '20px', background: '#fefaf8', textAlign: 'center', minHeight: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={product.image}
            alt={product.name}
            style={{
              maxWidth: '100%',
              maxHeight: '140px',
              objectFit: 'contain',
              transition: 'transform 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=400&q=80'; }}
          />
          {discountPercent > 0 && (
            <div style={{
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              background: '#dc2626',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '20px',
              fontSize: '0.7rem',
              fontWeight: 700
            }}>
              {discountPercent}% off
            </div>
          )}
          {product.variants && product.variants.length > 0 && (
            <div style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              background: '#2874f0',
              color: 'white',
              padding: '2px 8px',
              borderRadius: '20px',
              fontSize: '0.6rem',
              fontWeight: 600
            }}>
              {product.variants.length} Variants
            </div>
          )}
        </div>

        <div style={{ padding: '12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
              <span style={{ background: '#388e3c', color: 'white', padding: '2px 6px', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '2px' }}>
                {rating} <Star size={10} fill="white" />
              </span>
              <span style={{ fontSize: '0.7rem', color: '#878787' }}>({reviewCount})</span>
            </div>
          </div>

          <h3 style={{
            fontSize: '0.85rem',
            fontWeight: 500,
            margin: '0 0 4px',
            color: '#0f1111',
            lineHeight: '1.3',
            maxHeight: '2.6em',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {product.name}
          </h3>

          <div style={{ marginBottom: '6px' }}>
            <span style={{ fontSize: '1rem', fontWeight: 700, color: '#dc2626' }}>
              ₹{product.price?.toLocaleString()}
            </span>
            {product.oldPrice && (
              <span style={{ fontSize: '0.7rem', color: '#878787', textDecoration: 'line-through', marginLeft: '6px' }}>
                ₹{product.oldPrice?.toLocaleString()}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
            <Truck size={12} color="#dc2626" />
            <span style={{ fontSize: '0.6rem', color: '#878787' }}>Fresh Arrival</span>
          </div>

          <div style={{ display: 'flex', gap: '8px', marginTop: 'auto' }}>
            <button
              onClick={(e) => handleAddToCart(product, e)}
              style={{
                flex: 1,
                padding: '8px',
                background: '#ff9f00',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.7rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#f0a500'}
              onMouseLeave={(e) => e.target.style.background = '#ff9f00'}
            >
              <ShoppingCart size={12} /> Add to Cart
            </button>
            <button
              onClick={(e) => handleBuyNow(product, e)}
              style={{
                flex: 1,
                padding: '8px',
                background: '#fb641b',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '0.7rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'background 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#f35b1a'}
              onMouseLeave={(e) => e.target.style.background = '#fb641b'}
            >
              Buy Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Mobile Product Card
  const MobileProductCard = ({ product }) => {
    const discountPercent = product.oldPrice && product.oldPrice > product.price
      ? Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100) : 0;
    const rating = getProductRating(product);
    const reviewCount = getReviewCount();

    return (
      <div
        onClick={() => handleProductClick(product.id)}
        style={{
          display: 'flex',
          background: 'white',
          borderRadius: '12px',
          overflow: 'hidden',
          border: '1px solid rgba(220,38,38,0.1)',
          marginBottom: '12px',
          position: 'relative',
          cursor: 'pointer',
          transition: 'transform 0.2s'
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(4px)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
      >
        <div style={{ 
          position: 'relative', 
          width: '110px', 
          minWidth: '110px', 
          maxWidth: '110px',
          height: '110px',
          background: '#fefaf8', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden'
        }}>
          <img
            src={product.image}
            alt={product.name}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover'
            }}
            onError={(e) => { 
              e.target.src = 'https://images.unsplash.com/photo-1591886960571-74d43a9d4166?w=200&q=80'; 
            }}
          />
          {discountPercent > 0 && (
            <div style={{
              position: 'absolute',
              top: '4px',
              left: '4px',
              background: '#dc2626',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.6rem',
              fontWeight: 700,
              zIndex: 1
            }}>
              {discountPercent}% off
            </div>
          )}
        </div>
  
        <div style={{ flex: 1, padding: '10px' }}>
          <h3 style={{
            fontSize: '0.85rem',
            fontWeight: 500,
            margin: '0 0 4px',
            color: '#0f1111',
            lineHeight: '1.3',
            maxHeight: '2.6em',
            overflow: 'hidden',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}>
            {product.name}
          </h3>
  
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
            <span style={{ background: '#388e3c', color: 'white', padding: '2px 6px', borderRadius: '3px', fontSize: '0.65rem', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
              {rating} <Star size={9} fill="white" />
            </span>
            <span style={{ fontSize: '0.6rem', color: '#878787' }}>({reviewCount})</span>
          </div>
  
          <div style={{ marginBottom: '4px' }}>
            <span style={{ fontSize: '0.95rem', fontWeight: 700, color: '#dc2626' }}>
              ₹{product.price?.toLocaleString()}
            </span>
            {product.oldPrice && (
              <span style={{ fontSize: '0.65rem', color: '#878787', textDecoration: 'line-through', marginLeft: '6px' }}>
                ₹{product.oldPrice?.toLocaleString()}
              </span>
            )}
          </div>
  
          {discountPercent > 0 && (
            <p style={{ fontSize: '0.6rem', color: '#388e3c', marginBottom: '4px', fontWeight: 500 }}>
              Save ₹{(product.oldPrice - product.price).toLocaleString()}
            </p>
          )}
  
          <p style={{ fontSize: '0.55rem', color: '#878787', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '2px' }}>
            <Truck size={10} color="#dc2626" /> Fresh Arrival
          </p>
  
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={(e) => handleAddToCart(product, e)}
              style={{
                padding: '6px 12px',
                background: '#ff9f00',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.65rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
            >
              <ShoppingCart size={11} /> Add
            </button>
            <button
              onClick={(e) => handleBuyNow(product, e)}
              style={{
                padding: '6px 12px',
                background: '#fb641b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.65rem',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Buy Now
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (productsLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: '16px' }}>
        <Loader size={40} className="spinner" style={{ color: '#dc2626' }} />
        <p style={{ color: '#64748b', fontSize: '0.85rem' }}>Loading products...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#fff5f5', paddingBottom: '80px' }}>
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid rgba(220,38,38,0.1)', padding: '12px 16px', position: 'sticky', top: 0, zIndex: 100, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'white', fontWeight: 900, borderRadius: '8px', background: '#dc2626', padding: '6px 12px', textDecoration: 'none', fontSize: '0.85rem' }}>
            <ArrowLeft size={14} /> Back
          </Link>
          <div>
            <h1 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#1a0a0a' }}>{getSectionTitle()}</h1>
            <p style={{ fontSize: '0.7rem', color: '#64748b', margin: '2px 0 0' }}>{getSectionDescription()}</p>
          </div>
          <span style={{ marginLeft: 'auto', background: '#fff5f5', color: '#dc2626', padding: '4px 10px', borderRadius: '20px', fontSize: '0.7rem', fontWeight: 600 }}>
            {filteredProducts.length} items
          </span>
        </div>
      </div>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '12px' }}>
        
        {/* Search Bar with Meta Search */}
        <div ref={searchRef} style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '12px', 
          marginBottom: '16px', 
          border: '1px solid rgba(220,38,38,0.1)',
          position: 'relative'
        }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '150px' }}>
              <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#dc2626' }} />
              <input 
                type="text" 
                value={searchInput} 
                onChange={(e) => setSearchInput(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearchSubmit()}
                placeholder="Search products by name, description, or tag..." 
                style={{ width: '100%', padding: '8px 10px 8px 32px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '0.8rem', outline: 'none' }} 
              />
              {searchInput && (
                <button onClick={clearSearch} style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  <X size={12} />
                </button>
              )}
            </div>
            <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)} style={{ padding: '7px 24px 7px 10px', border: '1px solid #dc2626', borderRadius: '8px', fontSize: '0.75rem', background: '#fff5f5', color: '#dc2626', cursor: 'pointer', fontWeight: 500 }}>
              <option value="default">Sort by: Newest</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
              <option value="name-asc">Name: A to Z</option>
              <option value="discount">Discount: High to Low</option>
            </select>
            <button 
              onClick={() => setShowFilters(true)} 
              style={{ 
                display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', 
                border: '1px solid #dc2626', borderRadius: '8px', 
                background: selectedTags.length > 0 ? '#fff5f5' : 'white', 
                color: selectedTags.length > 0 ? '#dc2626' : '#475569', 
                cursor: 'pointer', fontSize: '0.75rem' 
              }}
            >
              <Filter size={14} /> Filter 
              {selectedTags.length > 0 && <span style={{ background: '#dc2626', color: 'white', borderRadius: '10px', padding: '0px 5px', fontSize: '0.65rem' }}>{selectedTags.length}</span>}
            </button>
          </div>

          {/* Search Suggestions Dropdown */}
          {showSuggestions && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
              zIndex: 1001,
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{ padding: '12px' }}>
                {recentSearches.length > 0 && (
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', padding: '0 8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={14} color="#dc2626" />
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>Recent Searches</span>
                      </div>
                      <button onClick={clearRecentSearches} style={{ fontSize: '10px', color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer' }}>Clear All</button>
                    </div>
                    {recentSearches.slice(0, 5).map((s, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => { setSearchInput(s); handleSearchSubmit(s); }} 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          width: '100%',
                          padding: '8px 12px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '8px'
                        }}
                      >
                        <Clock size={14} color="#dc2626" />
                        <span style={{ fontSize: '13px', color: '#212121' }}>{s}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                <div style={{ marginBottom: suggestions.length > 0 ? '16px' : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', padding: '0 8px' }}>
                    <TrendingUp size={14} color="#dc2626" />
                    <span style={{ fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>Popular Searches</span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {popularSearches.map((item, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => { setSearchInput(item.query); handleSearchSubmit(item.query); }} 
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '6px 12px',
                          background: '#fff5f5',
                          border: '1px solid #ffe0e0',
                          borderRadius: '30px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        <item.icon size={14} />
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                
                {suggestions.length > 0 && (
                  <div style={{ marginTop: '16px' }}>
                    <div style={{ padding: '0 8px', marginBottom: '8px', fontSize: '11px', fontWeight: 600, color: '#dc2626' }}>Products</div>
                    {suggestions.map((p, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => handleSuggestionClick(p)} 
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          width: '100%',
                          padding: '8px 12px',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          borderRadius: '8px'
                        }}
                      >
                        <img src={p.image} alt={p.name} style={{ width: '36px', height: '36px', borderRadius: '8px', objectFit: 'cover' }} />
                        <div style={{ flex: 1, textAlign: 'left' }}>
                          <div style={{ fontSize: '13px', fontWeight: 500, color: '#212121' }}>{p.name}</div>
                          <div style={{ fontSize: '11px', color: '#dc2626', fontWeight: 600 }}>₹{p.price?.toLocaleString()}</div>
                        </div>
                        <Search size={14} color="#dc2626" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {hasActiveFilters && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '10px', alignItems: 'center' }}>
              <span style={{ fontSize: '0.65rem', color: '#64748b' }}>Active filters:</span>
              {searchInput && <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: '12px', fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '4px' }}>Search: {searchInput}<X size={10} style={{ cursor: 'pointer' }} onClick={clearSearch} /></span>}
              {selectedTags.map(tag => <span key={tag} style={{ background: '#fff5f5', color: '#dc2626', padding: '2px 8px', borderRadius: '12px', fontSize: '0.65rem', display: 'flex', alignItems: 'center', gap: '4px' }}>{tag}<X size={10} style={{ cursor: 'pointer' }} onClick={() => handleTagToggle(tag)} /></span>)}
              <button onClick={clearFilters} style={{ background: 'none', border: 'none', color: '#dc2626', fontSize: '0.65rem', cursor: 'pointer', textDecoration: 'underline' }}>Clear all</button>
            </div>
          )}
          
          {isFiltering && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '10px', padding: '8px', background: '#fff5f5', borderRadius: '8px' }}>
              <Loader size={14} className="spinner" style={{ color: '#dc2626' }} />
              <span style={{ fontSize: '0.7rem', color: '#64748b' }}>Applying filters...</span>
            </div>
          )}
        </div>

        {/* Products Display */}
        {filteredProducts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', background: 'white', borderRadius: '16px', border: '1px solid rgba(220,38,38,0.1)' }}>
            <Search size={48} color="#cbd5e1" style={{ marginBottom: '16px' }} />
            <h3 style={{ fontSize: '1rem', color: '#1a0a0a', marginBottom: '8px' }}>No products found</h3>
            <p style={{ fontSize: '0.8rem', color: '#878787' }}>Try adjusting your search or filter criteria</p>
            <button onClick={clearFilters} style={{ marginTop: '20px', padding: '10px 24px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Clear All Filters</button>
          </div>
        ) : isMobile ? (
          <div>
            {filteredProducts.map(product => (
              <MobileProductCard key={product.id} product={product} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '20px' }}>
            {filteredProducts.map(product => (
              <DesktopProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </div>

      {/* Filter Modal (Mobile) */}
      {showFilters && (
        <>
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000 }} onClick={() => setShowFilters(false)} />
          <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'white', borderRadius: '20px 20px 0 0', padding: '20px', zIndex: 1001, maxHeight: '80vh', overflow: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 700, color: '#1a0a0a' }}>Filters</h3>
              <button onClick={() => setShowFilters(false)} style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: '10px', display: 'block', color: '#dc2626' }}>Sort By</label>
              <select value={sortBy} onChange={(e) => handleSortChange(e.target.value)} style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <option value="default">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
                <option value="discount">Discount: High to Low</option>
              </select>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: '10px', display: 'block', color: '#dc2626' }}>
                <DollarSign size={14} style={{ display: 'inline', marginRight: '4px' }} /> Price Range
              </label>
              <div style={{ display: 'flex', gap: '10px' }}>
                <input type="number" placeholder="Min" value={priceRange.min} onChange={(e) => setPriceRange({ ...priceRange, min: parseInt(e.target.value) || 0 })} style={{ flex: 1, padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                <span>-</span>
                <input type="number" placeholder="Max" value={priceRange.max} onChange={(e) => setPriceRange({ ...priceRange, max: parseInt(e.target.value) || 50000 })} style={{ flex: 1, padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
              </div>
            </div>
            
            {allTags.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontWeight: 600, fontSize: '0.8rem', marginBottom: '10px', display: 'block', color: '#dc2626' }}>
                  <Tag size={14} style={{ display: 'inline', marginRight: '4px' }} /> Product Tags
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {allTags.map(tag => (
                    <label key={tag} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '0.75rem', padding: '6px 12px', background: selectedTags.includes(tag) ? '#fff5f5' : '#f8fafc', borderRadius: '20px', border: selectedTags.includes(tag) ? '1px solid #dc2626' : '1px solid #e2e8f0' }}>
                      <input type="checkbox" checked={selectedTags.includes(tag)} onChange={() => handleTagToggle(tag)} style={{ width: '14px', height: '14px' }} />
                      {tag}
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', gap: '12px', marginTop: '20px' }}>
              <button onClick={clearFilters} style={{ flex: 1, padding: '10px', background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Clear All</button>
              <button onClick={() => setShowFilters(false)} style={{ flex: 1, padding: '10px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>Apply Filters</button>
            </div>
          </div>
        </>
      )}
      
      <style>{`
        .spinner {
          animation: spin 0.6s linear infinite;
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AllProductsPage;